import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  Alert,
  useMediaQuery,
  useTheme,
  Stack,
  CircularProgress,
  Backdrop,
  Divider,
  TextField,
  LinearProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import StairsIcon from "@mui/icons-material/Stairs";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  createOrUpdateDeliveryDetails,
  getMoveService,
  updateMoveService,
} from "../../redux/action/move-service";
import StairIcon from "../../assets/images/StairsIcon.png";
import ElevatorIcon from "../../assets/images/Elevator.png";
import NotAvailableIcon from "../../assets/images/N:A-Icon.png";
import Header from "../header/Header";
import { getCarrierByBusinessName } from "../../redux/action/carrier";
import StepProgress from "../stepsProgress/stepsProgress";

const DeliveryDetails = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const { canonicalId, carrierCanonicalId, businessName } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [dwellingType, setDwellingType] = useState<
    "home" | "apartment" | "storage" | ""
  >("");
  const [loadingMessage, setLoadingMessage] = useState("");
  const [floorNumber, setFloorNumber] = useState("");
  const [distance, setDistance] = useState("");
  const [color, setColor] = useState("#4A90E2");

  const [accessType, setAccessType] = useState<
    "elevator" | "stairs" | "not-applicable" | ""
  >("");
  const [moveId, setMoveId] = useState(null);
  const [floorNumberError, setFloorNumberError] = useState("");
  const lastStep = 2;
  useEffect(() => {
    if (canonicalId) {
      dispatch<any>(getMoveService(canonicalId))
        .then(unwrapResult)
        .then((res: any) => {
          setMoveId(res.id);
          setDistance(res.distance);
        })
        .catch((err: any) => {
          console.error("Failed to fetch move data:", err);
        });
    }
  }, [canonicalId, dispatch]);
  useEffect(() => {
    if (businessName) {
      dispatch<any>(getCarrierByBusinessName(businessName))
        .then(unwrapResult)
        .then((res: any) => {
          if (res.color_hex) {
            setColor(res.color_hex);
          }
        })
        .catch((err: any) => console.error("Failed to fetch carrier:", err))
        .finally(() => setIsLoading(false));
    }
  }, [businessName]);
  useEffect(() => {
    const updateLastStep = async () => {
      const moveServiceData = {
        canonical_id: canonicalId,
        last_step: lastStep,
      };
      await dispatch<any>(updateMoveService(moveServiceData))
        .then(unwrapResult)
        .then((res: any) => {});
    };

    if (canonicalId) {
      updateLastStep();
    }
  }, [canonicalId, dispatch, lastStep]);
  const handleContinue = async () => {
    if (!dwellingType) {
      setError("Please select a dwelling type");
      return;
    }

    if (dwellingType !== "home" && !accessType) {
      setError("Please select access type");
      return;
    }

    if (
      (dwellingType === "apartment" || dwellingType === "storage") &&
      accessType !== "not-applicable" &&
      !floorNumber
    ) {
      setError("Please enter floor number");
      return;
    }

    try {
      setIsLoading(true);
      setLoadingMessage("Processing your information...");

      setTimeout(async () => {
        try {
          const deliveryData = {
            dwelling_type: dwellingType,
            floor_number: accessType === "not-applicable" ? "0" : floorNumber,
            isElevator:
              accessType === "elevator"
                ? true
                : accessType === "stairs"
                ? false
                : null,
            moveService_id: moveId,
          };
          const res = await dispatch<any>(
            createOrUpdateDeliveryDetails(deliveryData)
          );
          unwrapResult(res);
          const distanceValue = parseFloat(distance);
          if (distanceValue > 100 && dwellingType !== "storage") {
            navigate(
              `/${businessName}/${carrierCanonicalId}/guidelines/${canonicalId}`
            );
          } else {
            navigate(
              `/${businessName}/${carrierCanonicalId}/bulky-items/${canonicalId}`
            );
          }
        } catch (err: any) {
          setError(err?.message || "Failed to save delivery details");
        } finally {
          setIsLoading(false);
        }
      }, 2000);
    } catch (err) {
      setIsLoading(false);
      setError("An unexpected error occurred");
    }
  };

  const AccessOption = ({
    type,
    icon,
    label,
  }: {
    type: "elevator" | "stairs" | "not-applicable";
    icon: string;
    label: string;
  }) => (
    <Button
      fullWidth
      variant={accessType === type ? "contained" : "outlined"}
      onClick={() => {
        setAccessType(type);
        if (type === "not-applicable") setFloorNumber("0");
      }}
      sx={{
        height: 120,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        borderColor: theme.palette.primary.main,
        "&:hover": {
          borderColor: theme.palette.primary.main,
        },
      }}
    >
      <img
        src={icon}
        alt={label}
        style={{
          width: 50,
          height: 50,
          filter: type === "stairs" ? "invert(1)" : "none",
        }}
      />
      <Typography fontSize={isMobile ? 11 : 14}>{label}</Typography>
    </Button>
  );

  const handleFloorNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    const numberValue = parseInt(value);
    if (numberValue > 20) {
      setFloorNumberError("Floor number cannot be greater than 20");
      setFloorNumber("20");
    } else {
      setFloorNumberError("");
      setFloorNumber(value);
    }
  };
  const handleBack = () => {
    navigate(-1);
  };
  return (
    <>
      <Header />
      <StepProgress currentStep={3} />
      <Container
        sx={{
          width: "100%",
          minHeight: "100vh",
          background: "#ffffff", // full page white
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          py: 2,
        }}
      >
        {/* main card */}
        <Box
          sx={{
            width: "100%",
            maxWidth: "900px",
            minHeight: isMobile ? "85vh" : "75vh",
            background: "#fff",
            borderRadius: "14px",
            boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", // paper pop feel
            p: isMobile ? 2 : 4,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          {isLoading && (
            <Backdrop
              sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={isLoading}
              onClick={() => {
                setIsLoading(true);
              }}
            >
              <Box sx={{ width: "50%" }}>
                <LinearProgress />
                <Typography
                  fontSize={16}
                  fontWeight={500}
                  textAlign="center"
                  mt={1}
                >
                  {loadingMessage}
                </Typography>
              </Box>
            </Backdrop>
          )}

          <Box mb={3}>
            <Typography
              fontWeight={700}
              fontSize={isMobile ? "18px" : "20px"}
              textAlign="center"
              mb={1}
            >
              Delivery Details
            </Typography>

            <Typography
              fontSize={isMobile ? "13px" : "15px"}
              textAlign="center"
              color="text.secondary"
              mb={3}
            >
              What type of dwelling are you moving to?
            </Typography>

            <Typography textAlign={"center"} fontWeight={600} mb={2}>
              Dwelling Type
            </Typography>

            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={2}
              justifyContent="center"
            >
              {[
                {
                  value: "home",
                  label: "Home",
                  desc: "Single-family house or townhome",
                  icon: "🏠",
                },
                {
                  value: "apartment",
                  label: "Apartment",
                  desc: "Apartment or multi-unit building",
                  icon: "🏢",
                },
                {
                  value: "storage",
                  label: "Storage",
                  desc: "Storage unit or warehouse",
                  icon: "📦",
                },
              ].map((opt: any) => {
                const selected = dwellingType === opt.value;
                return (
                  <Box
                    key={opt.value}
                    onClick={() => {
                      setDwellingType(opt.value);
                      setAccessType("");
                      setFloorNumber("");
                    }}
                    sx={{
                      flex: 1,
                      cursor: "pointer",
                      p: 3,
                      borderRadius: "12px",
                      border: selected
                        ? `2px solid ${color}`
                        : "1px solid #d1d5db",
                      background: selected ? `${color}20` : "#fff",
                      boxShadow: selected
                        ? "0 0 8px rgba(37,99,235,0.25)"
                        : "0 2px 6px rgba(0,0,0,0.06)",
                      transition: "0.2s",
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      "&:hover": { borderColor: `${color}` },
                    }}
                  >
                    {/* Radio indicator */}
                    <Box
                      sx={{
                        height: "18px",
                        width: "18px",
                        borderRadius: "50%",
                        border: `2px solid ${color}20`,
                        background: selected ? `${color}` : "#fff",
                        position: "absolute",
                        top: 12,
                        right: 12,
                      }}
                    />

                    {/* icon */}
                    <Typography fontSize="32px" mb={1}>
                      {opt.icon}
                    </Typography>

                    {/* label */}
                    <Typography fontWeight={600} fontSize="16px">
                      {opt.label}
                    </Typography>

                    {/* description */}
                    <Typography fontSize="13px" color="text.secondary" mt={0.5}>
                      {opt.desc}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>
          {/* Access Type (only for Apartment or Storage) */}
          {(dwellingType === "apartment" || dwellingType === "storage") && (
            <Box mt={3}>
              <Typography textAlign="center" fontWeight={600} mb={2}>
                Access Type
              </Typography>

              <Stack
                direction={isMobile ? "column" : "row"}
                spacing={2}
                justifyContent="center"
              >
                {[
                  { type: "stairs", label: "Stairs", icon: "🚶‍⬆️" },
                  { type: "elevator", label: "Elevator", icon: "🛗" },
                  {
                    type: "not-applicable",
                    label: "Not Applicable",
                    icon: "🚫",
                  },
                ].map((opt) => {
                  const selected = accessType === opt.type;
                  return (
                    <Box
                      key={opt.type}
                      onClick={() => {
                        setAccessType(opt.type as any);
                        if (opt.type === "not-applicable") setFloorNumber("0");
                        else setFloorNumber("");
                      }}
                      sx={{
                        flex: 1,
                        cursor: "pointer",
                        p: 3,
                        borderRadius: "12px",
                        border: selected
                          ? `2px solid ${color}`
                          : "1px solid #d1d5db",
                        background: selected ? `${color}20` : "#fff",
                        boxShadow: selected
                          ? "0 0 8px rgba(37,99,235,0.25)"
                          : "0 2px 6px rgba(0,0,0,0.06)",
                        transition: "0.2s",
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        "&:hover": { borderColor: `${color}` },
                      }}
                    >
                      {/* Radio indicator */}
                      <Box
                        sx={{
                          height: 18,
                          width: 18,
                          borderRadius: "50%",
                          border: `2px solid ${color}20`,
                          background: selected ? `${color}` : "#fff",
                          position: "absolute",
                          top: 12,
                          right: 12,
                        }}
                      />

                      {/* Icon */}
                      <Typography fontSize="36px" mb={1}>
                        {opt.icon}
                      </Typography>

                      {/* Label */}
                      <Typography fontWeight={600} fontSize="15px" mt={1}>
                        {opt.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>

              {/* Floor number (only when stairs/elevator) */}
              {accessType && accessType !== "not-applicable" && (
                <Box mt={3} maxWidth={isMobile ? "100%" : 360} mx="auto">
                  <Typography fontWeight={600} mb={1}>
                    Floor Number
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={floorNumber}
                    onChange={handleFloorNumberChange}
                    inputProps={{ min: 0, max: 20 }}
                    error={!!floorNumberError}
                    helperText={floorNumberError || "Enter 0 if ground level"}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Tip Box */}
          <Box
            sx={{
              background: "#f8f9fc", // light grey background
              borderRadius: "10px",
              border: "1px solid #e2e8f0",
              p: 2,
              mt: 2,
              mb: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center", // centers horizontally
              textAlign: "center", // centers multiline text
            }}
          >
            <Typography fontSize={14} color="text.secondary">
              💡 This helps us prepare the right equipment and plan access
              requirements for your delivery location.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: "20px",
              marginTop: "auto",
            }}
          >
            <Button
              variant="outlined"
              sx={{
                borderRadius: "20px",
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                padding: isMobile ? "8px 20px" : "8px 45px",
                textTransform: "none",
                fontSize: isMobile ? "12px" : "14px",
                fontWeight: "600",
              }}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              sx={{
                borderRadius: "20px",
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                padding: isMobile ? "8px 20px" : "8px 45px",
                textTransform: "none",
                fontSize: isMobile ? "12px" : "14px",
                fontWeight: "600",
                "&:hover": { backgroundColor: "#357ABD" },
                "&:disabled": {
                  backgroundColor: "#cccccc",
                  color: "#666666",
                },
              }}
              onClick={handleContinue}
              disabled={
                !dwellingType || (dwellingType !== "home" && !accessType)
              }
            >
              Continue
            </Button>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default DeliveryDetails;
